import React, { useContext } from "react";
import PropTypes from "prop-types";
import { EvaluationContext } from "../stores/EvaluationContext";
import { CatalogContext } from "../stores/CatalogContext";
import { DEFAULT_CRITERIA_COMPONENT_ID } from "../../../utils/transformCompleteCatalogToEditor";
import HelpText from "./HelpText";
import HeaderWithAnchor from "./HeaderWithAnchor";
import CompletionCheckMark from "./CompletionCheckMark";

/**
 * Single component (e.g. Web, Documentation) within a criterion. Ported from GSA openacr-editor Component.svelte.
 * Simplified: shows label and placeholder for level/notes; full form can be wired when evaluation shape is finalized.
 */
export default function Component({ chapterId, criteria, component }) {
  const { evaluation, setEvaluation } = useContext(EvaluationContext);
  const { getCatalog } = useContext(CatalogContext);
  const catalog = getCatalog(evaluation?.catalog);
  const components = catalog?.components ?? [];
  const conformanceLevels = catalog?.conformanceLevels ?? [];

  const currentComponent = components.find((c) => c.id === component);
  const chapterData = evaluation?.chapters?.[chapterId];
  const criteriaList = chapterData?.criteria ?? [];
  const currentEvaluationCriteria = criteriaList.find((c) => String(c.num) === String(criteria));
  const currentEvaluationComponent = currentEvaluationCriteria?.components?.find((c) => c.name === component);
  const disabled = chapterData?.disabled ? "disabled" : "";

  if (!currentComponent?.label) return null;

  const updateAdherence = (field, value) => {
    if (!setEvaluation || !chapterId) return;
    const critIndex = criteriaList.findIndex((c) => String(c.num) === String(criteria));
    if (critIndex === -1) return;
    const nextChapters = { ...evaluation.chapters };
    const nextChapter = { ...nextChapters[chapterId] };
    const nextCriteria = [...(nextChapter.criteria ?? [])];
    const nextCrit = nextCriteria[critIndex];
    const nextComps = [...(nextCrit?.components ?? [])];
    let compIndex = nextComps.findIndex((c) => c.name === component);
    if (compIndex === -1) {
      compIndex = nextComps.length;
      nextComps.push({ name: component, adherence: { level: "", notes: "" } });
    }
    const nextComp = { ...nextComps[compIndex], adherence: { ...nextComps[compIndex]?.adherence, [field]: value } };
    nextComps[compIndex] = nextComp;
    nextCriteria[critIndex] = { ...nextCrit, components: nextComps };
    nextChapter.criteria = nextCriteria;
    nextChapters[chapterId] = nextChapter;
    setEvaluation({ ...evaluation, chapters: nextChapters });
  };

  const adherence = currentEvaluationComponent?.adherence;
  const isDefaultComponent = component === DEFAULT_CRITERIA_COMPONENT_ID;
  const NOTES_MIN_LENGTH = 50;
  const notesLength = (adherence?.notes ?? "").length;
  const notesHasContent = notesLength > 0;
  const notesIsGood = notesLength >= NOTES_MIN_LENGTH;

  return (
    <div className="component my-2">
      {!isDefaultComponent && (
        <HeaderWithAnchor id={`${criteria}-${component}`} level={3}>
          {currentComponent.label}
        </HeaderWithAnchor>
      )}
      <label htmlFor={`${criteria}-${component}-level`} className="usa-label">
        Level
        <select
          id={`${criteria}-${component}-level`}
          className="usa-select"
          value={adherence?.level ?? ""}
          onChange={(e) => updateAdherence("level", e.target.value)}
          disabled={!!disabled}
        >
          <option value="">- Select -</option>
          {conformanceLevels.map((level) => (
            <option key={level.name} value={level.name}>
              {level.label}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={`${criteria}-${component}-notes`} className="usa-label">
        Remarks and Explanations
        {notesHasContent && (
          <span
            id={`${criteria}-${component}-notes-hint`}
            className={`usa-hint display-block ${notesIsGood ? "text-success" : "text-base"}`}
            aria-live="polite"
          >
            {notesIsGood ? (
              <>Good <CompletionCheckMark size="xs" label="Good length" /></>
            ) : (
              "Longer description may be helpful"
            )}
          </span>
        )}
        <textarea
          id={`${criteria}-${component}-notes`}
          className="usa-textarea"
          rows={2}
          value={adherence?.notes ?? ""}
          onChange={(e) => updateAdherence("notes", e.target.value)}
          disabled={!!disabled}
          aria-describedby={[
            notesHasContent ? `${criteria}-${component}-notes-hint` : null,
            `evaluation-${criteria}-${component}-notes-message`,
          ].filter(Boolean).join(" ")}
        />
      </label>
      <div id={`evaluation-${criteria}-${component}-notes-message`} className="notes-message text-sm italic" />
      <HelpText type="components" field="level" />
      <HelpText type="components" field="notes" />
    </div>
  );
}

Component.propTypes = {
  chapterId: PropTypes.string.isRequired,
  criteria: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  component: PropTypes.string.isRequired,
};
